import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Pencil, Plus, Trash2, X, Mail, Building2, FolderOpen, ClipboardSignature, GitBranch, Filter, Search, Users, UserPlus, FileSignature } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { AField, ATextArea, SectionShell, SaveButton, Notice, LoadError } from "./ui";
import { CustomerDocuments } from "./CustomerDocuments";
import { CustomerNotes } from "./CustomerNotes";
import { CustomerAgreements, AgreementStatusPill } from "./CustomerAgreements";
import { ApplicationsList } from "./ApplicationsList";
import { CreateApplicationModal } from "./CreateApplicationModal";
import { ProgressTab, CaseStatusChip, StatusLabel } from "./ProgressTab";
import { CustomerTasks } from "./CustomerTasks";
import { CustomerLenderQA } from "./CustomerLenderQA";
import { CustomerAppointments } from "./CustomerAppointments";
import { CustomerAvatar, CustomerAvatarEditor, AvatarLightbox } from "./CustomerAvatar";

const EMPTY_APPLICANT = { full_name: "", email: "", phone: "", dob: "", address: "" };
const EMPTY = {
  name: "", company: "", email: "", phone: "", address: "", joint_applicant_name: "",
  applicant_type: "single",
  applicant_1: null,
  applicant_2: null,
  joint_relationship: "",
};
const emptyJointForm = () => ({
  ...EMPTY,
  applicant_type: "joint",
  applicant_1: { ...EMPTY_APPLICANT },
  applicant_2: { ...EMPTY_APPLICANT },
});

const ProgressBadge = ({ summary }) => {
  if (!summary) return <span className="text-[11px] text-sage">—</span>;
  const { received, applicable } = summary;
  const pct = applicable ? Math.round((received / applicable) * 100) : 0;
  const complete = applicable > 0 && received >= applicable;
  return (
    <span
      className={`inline-flex items-center justify-center gap-1.5 min-w-[64px] text-[11.5px] font-bold tracking-tight px-2.5 py-1.5 rounded border whitespace-nowrap ${
        complete
          ? "bg-forest text-cream border-forest"
          : received > 0
            ? "bg-gold-soft/30 text-gold-dark border-gold/40"
            : "bg-cream-3 text-forest/60 border-forest/15"
      }`}
      title={`${received} of ${applicable} received · ${pct}%`}
    >
      <FolderOpen size={12} strokeWidth={1.9} />
      <span className="tabular-nums">{received}/{applicable}</span>
    </span>
  );
};

// ---- joint applicants section (new customer + convert flow) ----
const ApplicantFields = ({ idx, data, onChange, sameAddressNode }) => (
  <div className="space-y-4" data-testid={`applicant-${idx}-section`}>
    <div className="flex items-center justify-between">
      <h4 className="font-serif text-[16px] text-forest flex items-center gap-2">
        <Users size={14} strokeWidth={1.6} className="text-gold-dark" />
        Applicant {idx}
      </h4>
      {sameAddressNode}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <label className="block">
        <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Full name <span className="text-red-600">*</span></span>
        <input type="text" value={data.full_name || ""} onChange={(e) => onChange({ ...data, full_name: e.target.value })}
          data-testid={`applicant-${idx}-full-name`}
          className={`mt-1 w-full bg-white border px-3 py-2 text-sm focus:border-gold focus:outline-none ${(data.full_name || "").trim() ? "border-forest/15" : "border-amber-300 bg-amber-50/40"}`} />
      </label>
      <label className="block">
        <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Date of birth <span className="text-red-600">*</span></span>
        <input type="date" value={data.dob || ""} onChange={(e) => onChange({ ...data, dob: e.target.value })}
          data-testid={`applicant-${idx}-dob`}
          className={`mt-1 w-full bg-white border px-3 py-2 text-sm focus:border-gold focus:outline-none ${(data.dob || "").trim() ? "border-forest/15" : "border-amber-300 bg-amber-50/40"}`} />
      </label>
      <label className="block">
        <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Email <span className="text-red-600">*</span></span>
        <input type="email" value={data.email || ""} onChange={(e) => onChange({ ...data, email: e.target.value })}
          data-testid={`applicant-${idx}-email`}
          className={`mt-1 w-full bg-white border px-3 py-2 text-sm focus:border-gold focus:outline-none ${(data.email || "").trim() ? "border-forest/15" : "border-amber-300 bg-amber-50/40"}`} />
      </label>
      <label className="block">
        <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Telephone <span className="text-red-600">*</span></span>
        <input type="tel" value={data.phone || ""} onChange={(e) => onChange({ ...data, phone: e.target.value })}
          data-testid={`applicant-${idx}-phone`}
          className={`mt-1 w-full bg-white border px-3 py-2 text-sm focus:border-gold focus:outline-none ${(data.phone || "").trim() ? "border-forest/15" : "border-amber-300 bg-amber-50/40"}`} />
      </label>
    </div>
    <label className="block">
      <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Address <span className="text-red-600">*</span></span>
      <textarea rows={3} value={data.address || ""} onChange={(e) => onChange({ ...data, address: e.target.value })}
        data-testid={`applicant-${idx}-address`}
        className={`mt-1 w-full bg-white border px-3 py-2 text-sm focus:border-gold focus:outline-none ${(data.address || "").trim() ? "border-forest/15" : "border-amber-300 bg-amber-50/40"}`} />
    </label>
  </div>
);

const JointApplicantsSection = ({ form, setForm }) => {
  const a1 = form.applicant_1 || {};
  const a2 = form.applicant_2 || {};
  const sameAddress = Boolean(a1.address && a2.address && a1.address === a2.address);
  const toggleSame = () => {
    if (sameAddress) {
      setForm((f) => ({ ...f, applicant_2: { ...(f.applicant_2 || {}), address: "" } }));
    } else {
      setForm((f) => ({ ...f, applicant_2: { ...(f.applicant_2 || {}), address: f.applicant_1?.address || "" } }));
    }
  };
  return (
    <div className="space-y-6">
      <ApplicantFields
        idx={1}
        data={a1}
        onChange={(next) => setForm((f) => ({
          ...f,
          applicant_1: next,
          // mirror a2.address if "same address" is currently on so it stays in sync.
          applicant_2: sameAddress ? { ...(f.applicant_2 || {}), address: next.address || "" } : f.applicant_2,
        }))}
      />
      <hr className="border-forest/10" />
      <ApplicantFields
        idx={2}
        data={a2}
        onChange={(next) => setForm((f) => ({ ...f, applicant_2: next }))}
        sameAddressNode={
          <label className="flex items-center gap-2 text-[12.5px] text-forest cursor-pointer">
            <input type="checkbox" checked={sameAddress} onChange={toggleSame} data-testid="applicant-2-same-address" className="w-4 h-4 accent-gold" />
            Same address as Applicant 1
          </label>
        }
      />
      <hr className="border-forest/10" />
      <label className="block">
        <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Relationship between the two <span className="text-forest/40 normal-case tracking-normal font-normal">(e.g. spouse, siblings, business partners)</span></span>
        <input
          type="text"
          value={form.joint_relationship || ""}
          onChange={(e) => setForm((f) => ({ ...f, joint_relationship: e.target.value }))}
          data-testid="joint-relationship"
          className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none"
        />
      </label>
      <label className="block">
        <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Company (optional, shared)</span>
        <input type="text" value={form.company || ""} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
          className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
      </label>
    </div>
  );
};

// Modal shown when the admin clicks "New customer" — pick single vs joint.
const CustomerKindChooser = ({ onPick, onClose }) => (
  <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-5">
    <div className="bg-cream border border-forest/15 rounded-xl max-w-xl w-full p-7" data-testid="customer-kind-chooser">
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-serif text-xl text-forest">New customer</h3>
        <button type="button" onClick={onClose} className="p-1 text-forest/60 hover:text-forest"><X size={18} /></button>
      </div>
      <p className="text-[13px] text-sage mb-5">Single applicant or Joint applicants?</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => onPick("single")}
          data-testid="customer-kind-single"
          className="text-left bg-white border border-forest/15 hover:border-gold hover:bg-cream-3 rounded-lg p-5 transition-colors"
        >
          <UserPlus size={22} className="text-gold-dark mb-2" strokeWidth={1.6} />
          <p className="font-serif text-lg text-forest">Single applicant</p>
          <p className="text-[12.5px] text-sage mt-1">One person, one set of details — the standard setup.</p>
        </button>
        <button
          type="button"
          onClick={() => onPick("joint")}
          data-testid="customer-kind-joint"
          className="text-left bg-white border border-forest/15 hover:border-gold hover:bg-cream-3 rounded-lg p-5 transition-colors"
        >
          <Users size={22} className="text-gold-dark mb-2" strokeWidth={1.6} />
          <p className="font-serif text-lg text-forest">Joint applicants</p>
          <p className="text-[12.5px] text-sage mt-1">Two people applying together — captures both their details.</p>
        </button>
      </div>
    </div>
  </div>
);

export const CustomersSection = ({ initialTab = "customers", focus = null } = {}) => {
  const [list, setList] = useState(null);
  // State for the WhatsApp-style avatar lightbox. `preview` is null or
  // { customer, startApplicant } where startApplicant is 1 or 2.
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState({});
  const [caseSummary, setCaseSummary] = useState({});
  const [editing, setEditing] = useState(null); // null | "new" | customer
  const [tab, setTab] = useState("details"); // details | progress | applications | documents
  const [outerTab, setOuterTab] = useState(initialTab); // customers | applications
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [success, setSuccess] = useState("");
  const [creatingApp, setCreatingApp] = useState(false);
  const [customerAppReload, setCustomerAppReload] = useState(0);
  const [masterReload, setMasterReload] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | on_hold | cancelled | current:<step_id>
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      setLoadError("");
      const [{ data: customers }, { data: sum }, { data: cases }] = await Promise.all([
        axios.get(`${API}/admin/customers`, authHeaders()),
        axios.get(`${API}/admin/customers/documents-summary`, authHeaders()),
        axios.get(`${API}/admin/customers/case-summary`, authHeaders()),
      ]);
      setList(customers || []);
      setSummary(sum || {});
      setCaseSummary(cases || {});
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "The Customers list didn't load.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Honour an incoming `focus` directive (used when the Dashboard — or soon,
  // the Create Application wizard — needs to deep-link into a customer).
  useEffect(() => {
    const targetId = focus?.customerId || focus?.focusId;
    if (!targetId || !list) return;
    const c = list.find((x) => x.id === targetId);
    if (c) openEdit(c, focus.tab || "applications");
    // eslint-disable-next-line
  }, [focus, list]);

  const [chooser, setChooser] = useState(false); // Single-vs-Joint chooser for new customer

  const openNew = () => {
    setChooser(true);
  };
  const openNewAs = (kind) => {
    setChooser(false);
    setForm(kind === "joint" ? emptyJointForm() : EMPTY);
    setEditing("new");
    setTab("details");
    setError("");
    setSuccess("");
  };
  const openEdit = (c, initialInnerTab = "details") => {
    // Load existing record, hydrating empty applicant blocks for joint ones
    // so the form inputs always have strings (never undefined).
    const base = { ...EMPTY, ...c };
    if (base.applicant_type === "joint") {
      base.applicant_1 = { ...EMPTY_APPLICANT, ...(base.applicant_1 || {}) };
      base.applicant_2 = { ...EMPTY_APPLICANT, ...(base.applicant_2 || {}) };
    }
    setForm(base);
    setEditing(c);
    setTab(initialInnerTab);
    setError("");
    setSuccess("");
  };
  const convertToJoint = () => {
    // Pre-fill applicant 1 with the existing single-customer details so
    // nothing is lost — admin just needs to add applicant 2.
    setForm((f) => ({
      ...f,
      applicant_type: "joint",
      applicant_1: {
        full_name: f.name || "",
        email: f.email || "",
        phone: f.phone || "",
        dob: f.applicant_1?.dob || "",
        address: f.address || "",
      },
      applicant_2: { ...EMPTY_APPLICANT, ...(f.applicant_2 || {}) },
    }));
  };
  const close = () => {
    setEditing(null);
    setForm(EMPTY);
    setTab("details");
    // Refresh the summary so the badges stay in sync with any uploads/removals.
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company || "",
        email: form.email || "",
        phone: form.phone || "",
        address: form.address || "",
        joint_applicant_name: form.joint_applicant_name || "",
        applicant_type: form.applicant_type || "single",
        applicant_1: form.applicant_type === "joint" ? form.applicant_1 : null,
        applicant_2: form.applicant_type === "joint" ? form.applicant_2 : null,
        joint_relationship: form.applicant_type === "joint" ? (form.joint_relationship || "") : "",
      };
      if (payload.applicant_type === "joint") {
        // Mirror A1 into legacy top-level fields so every other feature
        // (invoices, PDFs, SA, forms) keeps working as if the customer were
        // a single applicant with the primary name/email/etc.
        payload.name = payload.applicant_1.full_name || payload.name;
        payload.email = payload.applicant_1.email || payload.email;
        payload.phone = payload.applicant_1.phone || payload.phone;
        payload.address = payload.applicant_1.address || payload.address;
        payload.joint_applicant_name = payload.applicant_2.full_name || "";
      }
      if (editing === "new") {
        await axios.post(`${API}/admin/customers`, payload, authHeaders());
        setSuccess("Customer saved.");
      } else {
        await axios.put(`${API}/admin/customers/${editing.id}`, payload, authHeaders());
        setSuccess("Customer updated.");
      }
      await load();
      close();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Invoices to this customer will keep their saved copy of the details.`)) return;
    try {
      await axios.delete(`${API}/admin/customers/${c.id}`, authHeaders());
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete");
    }
  };

  const sorted = useMemo(
    () => (list || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [list]
  );

  // Build a quick list of unique "current step" ids with labels + per-id
  // counts, for the filter chips.
  const currentSteps = useMemo(() => {
    const seen = new Map();
    const counts = new Map();
    Object.values(caseSummary || {}).forEach((cs) => {
      if (cs?.case_status === "active" && cs?.current_step_id) {
        const label = (cs.progress_label || "").split(" · ")[0];
        if (!seen.has(cs.current_step_id)) seen.set(cs.current_step_id, label);
        counts.set(cs.current_step_id, (counts.get(cs.current_step_id) || 0) + 1);
      }
    });
    return Array.from(seen.entries()).map(([id, label]) => ({
      id, label, count: counts.get(id) || 0,
    }));
  }, [caseSummary]);

  // Counts for the top-level filter chips.
  const statusCounts = useMemo(() => {
    const out = { all: sorted.length, active: 0, on_hold: 0, cancelled: 0 };
    sorted.forEach((c) => {
      const cs = caseSummary[c.id];
      if (!cs) return;
      if (cs.case_status === "active") out.active += 1;
      else if (cs.case_status === "on_hold") out.on_hold += 1;
      else if (cs.case_status === "cancelled") out.cancelled += 1;
    });
    return out;
  }, [sorted, caseSummary]);

  // Agreement summary {customer_id: {status, count, latest_signed_at_dmy}} —
  // used to show a small Service Agreement pill in the Customers list.
  const [agreementSummary, setAgreementSummary] = useState({});
  useEffect(() => {
    axios
      .get(`${API}/admin/service-agreements/summary`, authHeaders())
      .then(({ data }) => setAgreementSummary(data || {}))
      .catch(() => {});
  }, []);

  // Per-customer application status derived from GET /admin/forms. We prefer
  // "signed" over "in_progress" when a customer has multiple forms. Nothing
  // is shown when a customer has no linked application.
  const [applicationSummary, setApplicationSummary] = useState({});
  useEffect(() => {
    axios
      .get(`${API}/admin/forms`, authHeaders())
      .then(({ data }) => {
        const by = {};
        (data || []).forEach((f) => {
          if (!f.customer_id) return;
          const cur = by[f.customer_id];
          // "signed" > "in_progress"
          if (!cur || (cur !== "signed" && f.status === "signed")) {
            by[f.customer_id] = f.status || "in_progress";
          }
        });
        setApplicationSummary(by);
      })
      .catch(() => {});
  }, []);

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((c) => {
      if (statusFilter !== "all") {
        const cs = caseSummary[c.id];
        if (!cs) return false;
        if (statusFilter === "active" && cs.case_status !== "active") return false;
        if (statusFilter === "on_hold" && cs.case_status !== "on_hold") return false;
        if (statusFilter === "cancelled" && cs.case_status !== "cancelled") return false;
        if (statusFilter.startsWith("current:")) {
          if (!(cs.case_status === "active" && cs.current_step_id === statusFilter.slice(8))) return false;
        }
      }
      if (!q) return true;
      const hay = `${c.name || ""} ${c.company || ""} ${c.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [sorted, statusFilter, caseSummary, query]);

  const headerActions = !editing && (
    <div className="flex gap-2">
      <button
        type="button"
        data-testid="customers-new-application-button"
        onClick={() => setCreatingApp(true)}
        className="inline-flex items-center gap-2 bg-white border border-forest/25 hover:border-forest text-forest px-4 py-2.5 rounded text-[11px] font-bold tracking-[0.2em] uppercase transition-colors"
      >
        <ClipboardSignature size={14} strokeWidth={2.1} /> Create application
      </button>
      <button
        type="button"
        data-testid="customers-new-button"
        onClick={openNew}
        className="btn-gold"
      >
        <Plus size={14} strokeWidth={2.2} /> New Customer
      </button>
    </div>
  );

  return (
    <SectionShell
      title="Customers"
      subtitle="Save customer details once, then raise invoices, run calculator scenarios and send application forms."
      testid="admin-customers-section"
      action={headerActions}
    >
      <Notice error={error} success={success} />

      {!editing && (
        <>
          {/* Outer tabs: Customers | Applications */}
          <div className="flex gap-1 border-b border-forest/15 mb-6">
            {[
              { id: "customers", label: "Customers" },
              { id: "applications", label: "Applications" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`customers-outer-tab-${t.id}`}
                onClick={() => setOuterTab(t.id)}
                className={`px-4 py-2.5 text-[11px] font-bold tracking-[0.18em] uppercase border-b-2 -mb-px transition-colors ${
                  outerTab === t.id ? "border-gold text-forest" : "border-transparent text-forest/50 hover:text-forest"
                }`}
              >{t.label}</button>
            ))}
          </div>

          {outerTab === "customers" && loadError && (
            <LoadError
              testid="customers-load-error"
              onRetry={load}
              message={loadError}
            />
          )}

          {outerTab === "customers" && !loadError && (
            <>
              {list === null && <p className="text-sm text-sage">Loading…</p>}
              {list && list.length === 0 && (
                <p className="text-sm text-sage bg-white border border-forest/10 rounded px-6 py-10 text-center">
                  No customers yet — add your first.
                </p>
              )}
              {list && list.length > 0 && (
                <>
                {/* Filter bar */}
                <div className="space-y-3 mb-5" data-testid="customers-filter-bar">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[220px]">
                      <Search size={14} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
                      <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="customers-search-input"
                        placeholder="Search by name, company or email…"
                        className="w-full bg-white border border-forest/15 rounded pl-9 pr-3 py-2 text-[13px] focus:border-gold focus:outline-none"
                      />
                    </div>
                    {filteredSorted.length !== sorted.length && (
                      <span className="text-[11px] text-sage">Showing {filteredSorted.length} of {sorted.length}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.18em] uppercase text-forest/60 mr-1"><Filter size={12} strokeWidth={1.8} /> Filter</span>
                    {[
                      { v: "all", label: "All", count: statusCounts.all, cls: "border-forest/20 text-forest/70" },
                      { v: "active", label: "Active", count: statusCounts.active, cls: "border-forest/20 text-forest/70" },
                      { v: "on_hold", label: "On hold", count: statusCounts.on_hold, cls: "border-amber-200 text-amber-800" },
                      { v: "cancelled", label: "Cancelled", count: statusCounts.cancelled, cls: "border-red-200 text-red-700" },
                    ].map((f) => {
                      const active = statusFilter === f.v;
                      return (
                        <button
                          key={f.v}
                          type="button"
                          onClick={() => setStatusFilter(f.v)}
                          data-testid={`customers-filter-${f.v}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-semibold tracking-tight rounded-full border transition-colors ${
                            active ? "bg-forest text-cream border-forest" : `bg-white ${f.cls} hover:border-forest`
                          }`}
                        >
                          {f.label}
                          <span className={`text-[10.5px] font-bold rounded-full px-1.5 ${active ? "bg-cream/15 text-cream" : "bg-cream-3 text-forest/60"}`}>{f.count}</span>
                        </button>
                      );
                    })}
                    {currentSteps.map((s) => {
                      const key = `current:${s.id}`;
                      const active = statusFilter === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setStatusFilter(key)}
                          data-testid={`customers-filter-step-${s.id}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-semibold tracking-tight rounded-full border transition-colors ${
                            active ? "bg-gold text-forest border-gold" : "bg-white border-gold/40 text-gold-dark hover:border-gold"
                          }`}
                        >
                          {s.label}
                          <span className={`text-[10.5px] font-bold rounded-full px-1.5 ${active ? "bg-forest/15 text-forest" : "bg-gold-soft/40 text-gold-dark"}`}>{s.count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {filteredSorted.length === 0 ? (
                  <p className="text-sm text-sage bg-white border border-forest/10 rounded px-6 py-10 text-center" data-testid="customers-empty-filtered">
                    No customers match your search.
                  </p>
                ) : (<>
                <div className="hidden md:block bg-white border border-forest/10 rounded-xl overflow-hidden">
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr className="border-b border-forest/10 text-[10.5px] tracking-[0.18em] uppercase text-forest/60 bg-cream-3/40">
                        <th className="text-left px-5 py-3 font-bold w-[24%]">Name</th>
                        <th className="text-left px-4 py-3 font-bold w-[30%]">Status</th>
                        <th className="text-left px-4 py-3 font-bold w-[22%]">Email</th>
                        <th className="text-center px-3 py-3 font-bold w-[10%]">Docs</th>
                        <th className="text-right pl-3 pr-5 py-3 font-bold w-[14%]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSorted.map((c, i) => (
                        <tr
                          key={c.id}
                          data-testid={`customers-row-${i + 1}`}
                          className="border-b border-forest/5 last:border-b-0 hover:bg-cream-3/50 transition-colors align-top"
                        >
                          <td className="px-5 py-5">
                            <div className="flex items-start gap-3">
                              <CustomerAvatar
                                customer={c}
                                size="md"
                                testid={`customers-avatar-${i + 1}`}
                                onClick={(applicant) => setPreview({ customer: c, startApplicant: applicant })}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-semibold text-forest">{c.name}</p>
                                  {c.applicant_type === "joint" && (
                                    <span
                                      data-testid={`customers-joint-pill-${i + 1}`}
                                      title={c.joint_applicant_name ? `Joint with ${c.joint_applicant_name}` : "Joint applicants"}
                                      className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.16em] uppercase bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded"
                                    >
                                      <Users size={10} strokeWidth={1.8} /> Joint
                                    </span>
                                  )}
                                </div>
                                {c.applicant_type === "joint" && c.joint_applicant_name && (
                                  <p className="text-[11px] text-sage mt-0.5">with {c.joint_applicant_name}</p>
                                )}
                                {c.company && (
                                  <p className="text-[11px] text-sage mt-0.5 inline-flex items-center gap-1.5"><Building2 size={11} strokeWidth={1.6} /> {c.company}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-5">
                            <button
                              type="button"
                              onClick={() => openEdit(c, "progress")}
                              data-testid={`customers-status-${i + 1}`}
                              className="text-left w-full group min-h-[72px] flex flex-col justify-between gap-2"
                              title="Open progress"
                            >
                              <CaseStatusChip summary={caseSummary[c.id]} />
                              {/* Fixed-position label strip so every row lines up */}
                              <div className="flex items-center gap-1.5 flex-wrap min-h-[22px]">
                                <StatusLabel
                                  kind="application"
                                  status={applicationSummary[c.id]}
                                  testid={`customers-application-label-${i + 1}`}
                                />
                                <StatusLabel
                                  kind="agreement"
                                  status={agreementSummary[c.id]?.status}
                                  testid={`customers-agreement-label-${i + 1}`}
                                />
                              </div>
                            </button>
                          </td>
                          <td className="px-4 py-5 text-sage">
                            {c.email ? (
                              <span className="inline-flex items-center gap-1.5 min-w-0 truncate">
                                <Mail size={13} strokeWidth={1.6} /> <span className="truncate">{c.email}</span>
                              </span>
                            ) : "—"}
                          </td>
                          <td className="px-3 py-5">
                            <div className="flex justify-center">
                              <button
                                type="button"
                                onClick={() => openEdit(c, "documents")}
                                data-testid={`customers-docs-badge-${i + 1}`}
                                className="hover:opacity-80 transition-opacity"
                              >
                                <ProgressBadge summary={summary[c.id]} />
                              </button>
                            </div>
                          </td>
                          <td className="pl-3 pr-5 py-5">
                            <div className="flex justify-end items-center gap-1.5 whitespace-nowrap">
                              {[
                                { id: "progress", title: "Progress", Icon: GitBranch, tab: "progress" },
                                { id: "apps", title: "Application", Icon: ClipboardSignature, tab: "applications" },
                                { id: "agreement", title: "Agreement", Icon: FileSignature, tab: "agreements" },
                                { id: "docs", title: "Documents", Icon: FolderOpen, tab: "documents" },
                                { id: "edit", title: "Edit", Icon: Pencil, tab: null },
                              ].map(({ id, title, Icon, tab }) => (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => openEdit(c, tab || "details")}
                                  data-testid={`customers-${id}-${i + 1}`}
                                  className="group relative p-1.5 text-forest/70 hover:text-forest hover:bg-cream-3 rounded transition-colors"
                                >
                                  <Icon size={15} strokeWidth={1.6} />
                                  <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-forest text-cream text-[10px] tracking-[0.1em] uppercase px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                    {title}
                                  </span>
                                </button>
                              ))}
                              <span className="w-px h-5 bg-forest/15 mx-1.5 shrink-0" aria-hidden />
                              <button
                                type="button"
                                onClick={() => remove(c)}
                                data-testid={`customers-delete-${i + 1}`}
                                className="group relative p-1.5 text-rose-500/80 hover:text-rose-700 hover:bg-rose-50 rounded transition-colors"
                              >
                                <Trash2 size={15} strokeWidth={1.6} />
                                <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-rose-700 text-white text-[10px] tracking-[0.1em] uppercase px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                  Delete
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {filteredSorted.map((c, i) => (
                    <div key={c.id} data-testid={`customers-card-${i + 1}`} className="bg-white border border-forest/10 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 flex items-start gap-3">
                          <CustomerAvatar
                            customer={c}
                            size="md"
                            testid={`customers-card-avatar-${i + 1}`}
                            onClick={(applicant) => setPreview({ customer: c, startApplicant: applicant })}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-forest truncate">{c.name}</p>
                              {c.applicant_type === "joint" && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.16em] uppercase bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded"><Users size={10} strokeWidth={1.8}/> Joint</span>
                              )}
                            </div>
                            {c.applicant_type === "joint" && c.joint_applicant_name && (
                              <p className="text-[11px] text-sage mt-0.5">with {c.joint_applicant_name}</p>
                            )}
                            {c.company && <p className="text-[12px] text-sage mt-0.5 truncate inline-flex items-center gap-1.5"><Building2 size={12} strokeWidth={1.6} /> {c.company}</p>}
                            {c.email && <p className="text-[12px] text-sage mt-1 break-all inline-flex items-center gap-1.5"><Mail size={12} strokeWidth={1.6} /> {c.email}</p>}
                            <div className="mt-3 space-y-2">
                              <CaseStatusChip summary={caseSummary[c.id]} compact />
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <StatusLabel kind="application" status={applicationSummary[c.id]} />
                                <StatusLabel kind="agreement" status={agreementSummary[c.id]?.status} />
                              </div>
                              <div className="pt-1">
                                <ProgressBadge summary={summary[c.id]} />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-0.5 shrink-0 flex-wrap justify-end">
                          <button type="button" onClick={() => openEdit(c, "progress")} className="p-2 text-forest/70 hover:text-forest hover:bg-cream-3 rounded" title="Progress"><GitBranch size={16} strokeWidth={1.6} /></button>
                          <button type="button" onClick={() => openEdit(c, "applications")} className="p-2 text-forest/70 hover:text-forest hover:bg-cream-3 rounded" title="Application"><ClipboardSignature size={16} strokeWidth={1.6} /></button>
                          <button type="button" onClick={() => openEdit(c, "agreements")} className="p-2 text-forest/70 hover:text-forest hover:bg-cream-3 rounded" title="Agreement"><FileSignature size={16} strokeWidth={1.6} /></button>
                          <button type="button" onClick={() => openEdit(c, "documents")} className="p-2 text-forest/70 hover:text-forest hover:bg-cream-3 rounded" title="Documents"><FolderOpen size={16} strokeWidth={1.6} /></button>
                          <button type="button" onClick={() => openEdit(c)} className="p-2 text-forest/70 hover:text-forest hover:bg-cream-3 rounded" title="Edit"><Pencil size={16} strokeWidth={1.6} /></button>
                          <button type="button" onClick={() => remove(c)} className="p-2 text-rose-500/80 hover:text-rose-700 hover:bg-rose-50 rounded" title="Delete"><Trash2 size={16} strokeWidth={1.6} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </>)}
                </>
              )}
            </>
          )}

          {outerTab === "applications" && (
            <div data-testid="customers-master-applications">
              <ApplicationsList
                customers={list || []}
                reloadKey={masterReload}
                emptyMessage="No applications yet — tap “Create application” to start one."
              />
            </div>
          )}
        </>
      )}

      {chooser && (
        <CustomerKindChooser
          onPick={openNewAs}
          onClose={() => setChooser(false)}
        />
      )}

      {editing && (
        <div className="bg-white border border-forest/10 rounded-xl p-6 sm:p-8 space-y-6 w-full" data-testid="customers-form">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              {editing !== "new" && (
                <CustomerAvatar
                  customer={editing}
                  size="xl"
                  testid="customers-form-avatar"
                  onClick={(applicant) => setPreview({ customer: editing, startApplicant: applicant })}
                />
              )}
              <h3 className="font-serif text-xl text-forest truncate">
                {editing === "new" ? "New Customer" : editing.name}
              </h3>
            </div>
            <button
              type="button"
              onClick={close}
              className="p-2 text-forest/60 hover:text-forest"
              data-testid="customers-close-form"
              aria-label="Close editor"
            >
              <X size={18} strokeWidth={1.8} />
            </button>
          </div>

          {editing !== "new" && (
            <div className="flex flex-wrap gap-x-1 gap-y-0 border-b border-forest/15 -mx-6 sm:-mx-8 px-6 sm:px-8">
              {[
                { id: "details", label: "Details" },
                { id: "progress", label: "Progress" },
                { id: "applications", label: "Applications" },
                { id: "agreements", label: "Agreements" },
                { id: "documents", label: "Documents" },
                { id: "tasks", label: "Tasks" },
                { id: "appointments", label: "Appointments" },
                { id: "lender_qa", label: "Lender Q&A" },
                { id: "notes", label: "Notes" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`customer-tab-${t.id}`}
                  className={`px-3 py-2.5 text-[11.5px] font-bold tracking-[0.1em] uppercase border-b-2 transition-colors -mb-px whitespace-nowrap ${
                    tab === t.id
                      ? "border-gold text-forest"
                      : "border-transparent text-forest/50 hover:text-forest"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {(editing === "new" || tab === "details") && (
            <form onSubmit={save} className="space-y-5">
              {/* Mode badge + convert control */}
              <div className="flex items-center justify-between flex-wrap gap-3 pb-2 border-b border-forest/8">
                <span
                  data-testid="customer-applicant-type-badge"
                  className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 border rounded ${
                    form.applicant_type === "joint"
                      ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-cream-3 text-forest/70 border-forest/15"
                  }`}
                >
                  <Users size={12} strokeWidth={1.8} />
                  {form.applicant_type === "joint" ? "Joint applicants" : "Single applicant"}
                </span>
                {editing !== "new" && form.applicant_type === "single" && (
                  <button type="button" onClick={convertToJoint} data-testid="customer-convert-joint-btn" className="text-[12px] font-bold tracking-[0.12em] uppercase text-gold-dark hover:text-forest underline underline-offset-4">
                    Convert to joint applicants
                  </button>
                )}
                {form.applicant_type === "joint" && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, applicant_type: "single", applicant_1: null, applicant_2: null, joint_relationship: "" }))}
                    data-testid="customer-switch-single-btn"
                    className="text-[12px] font-bold tracking-[0.12em] uppercase text-forest/60 hover:text-forest underline underline-offset-4"
                  >
                    Switch back to single
                  </button>
                )}
              </div>

              {/* Avatar editor — one for single, two for joint. Only on
                   EXISTING customers because new ones have no id yet. */}
              {editing !== "new" && (
                <div className="bg-cream-3/40 border border-forest/10 rounded-lg px-5 py-4 flex items-center gap-6 flex-wrap" data-testid="customer-avatar-editors">
                  <div>
                    <p className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-forest/70 mb-2">
                      Profile photo{form.applicant_type === "joint" ? "s" : ""}
                    </p>
                    <p className="text-[11.5px] text-sage max-w-[22rem]">
                      Only visible to logged-in team members. Customers never see these photos.
                    </p>
                  </div>
                  <div className="flex gap-6 flex-wrap">
                    <CustomerAvatarEditor
                      customer={editing}
                      applicant={1}
                      size="lg"
                      onChanged={async () => {
                        await load();
                        // Sync the open modal so it immediately reflects the change.
                        try {
                          const { data } = await axios.get(`${API}/admin/customers`, authHeaders());
                          const fresh = (data || []).find((x) => x.id === editing.id);
                          if (fresh) setEditing(fresh);
                        } catch { /* fall back to next load */ }
                      }}
                    />
                    {form.applicant_type === "joint" && (
                      <CustomerAvatarEditor
                        customer={editing}
                        applicant={2}
                        size="lg"
                        onChanged={async () => {
                          await load();
                          try {
                            const { data } = await axios.get(`${API}/admin/customers`, authHeaders());
                            const fresh = (data || []).find((x) => x.id === editing.id);
                            if (fresh) setEditing(fresh);
                          } catch { /* fall back */ }
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {form.applicant_type === "single" ? (
                <>
                  <AField label="Full Name" testid="customer-field-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <AField label="Company" testid="customer-field-company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
                    <AField label="Email" testid="customer-field-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} hint="Used to email invoices directly to the customer." />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <AField label="Telephone" testid="customer-field-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} hint="Shown on the Service Agreement." />
                    <AField label="Joint applicant name" testid="customer-field-joint" value={form.joint_applicant_name} onChange={(e) => setForm((f) => ({ ...f, joint_applicant_name: e.target.value }))} hint="Only used if you don't convert to joint. Prefer the Convert to joint button above." />
                  </div>
                  <ATextArea label="Billing Address" testid="customer-field-address" rows={4} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} hint="Appears on invoices under the customer name." />
                </>
              ) : (
                <JointApplicantsSection form={form} setForm={setForm} />
              )}
              <div className="flex gap-3">
                <SaveButton saving={saving} testid="customer-save-button" />
                <button
                  type="button"
                  onClick={close}
                  className="btn-ghost-dark"
                  data-testid="customer-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {editing !== "new" && tab === "progress" && (
            <ProgressTab
              customerId={editing.id}
              customerName={editing.name}
              onChanged={load}
            />
          )}

          {editing !== "new" && tab === "applications" && (
            <div data-testid="customer-applications-panel" className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[13px] text-sage">All mortgage and remortgage applications for {editing.name}.</p>
                <button
                  type="button"
                  onClick={() => setCreatingApp(true)}
                  data-testid="customer-applications-new"
                  className="inline-flex items-center gap-2 bg-forest hover:bg-forest-soft text-cream px-4 py-2.5 rounded text-[11px] font-bold tracking-[0.2em] uppercase transition-colors"
                >
                  <Plus size={14} strokeWidth={2.2} /> New application
                </button>
              </div>
              <ApplicationsList
                filterCustomerId={editing.id}
                customers={list || []}
                reloadKey={customerAppReload}
                emptyMessage={`No applications yet for ${editing.name}.`}
              />
            </div>
          )}

          {editing !== "new" && tab === "documents" && (
            <CustomerDocuments customerId={editing.id} />
          )}

          {editing !== "new" && tab === "agreements" && (
            <CustomerAgreements customerId={editing.id} customer={editing} />
          )}

          {editing !== "new" && tab === "tasks" && (
            <CustomerTasks customerId={editing.id} customerName={editing.name} />
          )}

          {editing !== "new" && tab === "appointments" && (
            <CustomerAppointments customerId={editing.id} customerName={editing.name} />
          )}

          {editing !== "new" && tab === "lender_qa" && (
            <CustomerLenderQA customerId={editing.id} customerName={editing.name} />
          )}

          {editing !== "new" && tab === "notes" && (
            <CustomerNotes customerId={editing.id} />
          )}
        </div>
      )}

      {creatingApp && (
        <CreateApplicationModal
          preselectedCustomerId={editing && editing !== "new" ? editing.id : null}
          onClose={() => setCreatingApp(false)}
          onCreated={() => {
            // Refresh master + per-customer lists and reload customers (case
            // summary + fresh customer records) because creating a form
            // auto-ticks "Application sent to client".
            setMasterReload((v) => v + 1);
            setCustomerAppReload((v) => v + 1);
            load();
          }}
        />
      )}

      {preview && (
        <AvatarLightbox
          customer={preview.customer}
          startApplicant={preview.startApplicant}
          onClose={() => setPreview(null)}
          onChanged={async () => {
            // Refresh customer list so avatars update everywhere. If the
            // open modal matches the previewed customer, refresh its copy
            // so the modal reflects the new photo without reopening.
            try {
              const { data } = await axios.get(`${API}/admin/customers`, authHeaders());
              setList(data);
              setPreview((p) => {
                if (!p) return null;
                const fresh = (data || []).find((x) => x.id === p.customer.id);
                return fresh ? { ...p, customer: fresh } : p;
              });
              if (editing && editing !== "new") {
                const freshEd = (data || []).find((x) => x.id === editing.id);
                if (freshEd) setEditing(freshEd);
              }
            } catch { /* fall back */ }
          }}
        />
      )}
    </SectionShell>
  );
};
